import { logger } from '../../logger.js';
import { isDaemonRunning } from '../client.js';
import type { DaemonWatcher } from './types.js';

export interface DaemonWatcherOptions {
  port: number;
  /** How often to ping /health (ms). */
  pollIntervalMs?: number;
  /** How long a new state must persist before it's reported as "stable" (ms). */
  stabilityMs?: number;
}

/**
 * Polls the daemon /health endpoint and reports *stable* state changes.
 *
 * Strategy:
 *   - Poll every pollIntervalMs (default 10s).
 *   - When observed state differs from the last reported state, start a stability timer.
 *   - Confirm /health again when stabilityMs expires before emitting the change.
 *   - If the state flips back before the stability window closes, cancel — no event.
 *
 * Initial state (first poll) is reported without delay so startup is fast.
 */
export class PollingDaemonWatcher implements DaemonWatcher {
  private readonly port: number;
  private readonly pollIntervalMs: number;
  private readonly stabilityMs: number;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;
  private currentReported: boolean | null = null;
  private pendingState: boolean | null = null;
  private stabilityGeneration = 0;
  private confirmingGeneration: number | null = null;
  private subscribers: Array<(state: boolean) => void> = [];
  private stopped = false;

  constructor(opts: DaemonWatcherOptions) {
    this.port = opts.port;
    this.pollIntervalMs = opts.pollIntervalMs ?? 10_000;
    this.stabilityMs = opts.stabilityMs ?? 30_000;
  }

  getCurrentState(): boolean {
    return this.currentReported === true;
  }

  onStableChange(cb: (daemonActive: boolean) => void): void {
    this.subscribers.push(cb);
  }

  async start(): Promise<void> {
    if (this.pollTimer) return;
    // Initial poll, no debounce — set the baseline immediately.
    const initial = await isDaemonRunning(this.port).catch(() => false);
    this.currentReported = initial;
    this.pendingState = initial;
    logger.debug({ initial, port: this.port }, 'DaemonWatcher: initial state');
    this.pollTimer = setInterval(() => {
      void this.tick();
    }, this.pollIntervalMs);
    this.pollTimer.unref();
  }

  stop(): void {
    this.stopped = true;
    this.stabilityGeneration++;
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.stabilityTimer) clearTimeout(this.stabilityTimer);
    this.pollTimer = null;
    this.stabilityTimer = null;
  }

  private async tick(): Promise<void> {
    if (this.stopped) return;
    const state = await isDaemonRunning(this.port).catch(() => false);
    if (this.stopped) return;

    if (state === this.currentReported) {
      // Same as currently-reported: cancel any pending flip.
      this.stabilityGeneration++;
      if (this.stabilityTimer) {
        clearTimeout(this.stabilityTimer);
        this.stabilityTimer = null;
      }
      this.pendingState = state;
      return;
    }

    if (
      state === this.pendingState &&
      (this.stabilityTimer || this.confirmingGeneration === this.stabilityGeneration)
    ) {
      // The timer or its confirmation probe is still running for this window.
      return;
    }

    // State differs from current, and is either new or reversal of a pending reversal.
    this.pendingState = state;
    const generation = ++this.stabilityGeneration;
    if (this.stabilityTimer) clearTimeout(this.stabilityTimer);
    this.stabilityTimer = setTimeout(() => {
      this.stabilityTimer = null;
      this.confirmingGeneration = generation;
      void this.confirmStableChange(state, generation);
    }, this.stabilityMs);
    this.stabilityTimer.unref?.();
  }

  private async confirmStableChange(expected: boolean, generation: number): Promise<void> {
    try {
      if (
        this.stopped ||
        generation !== this.stabilityGeneration ||
        this.currentReported === expected
      )
        return;
      // A blocked event loop can delay both the interval poll and this timer.
      // One failed 500 ms /health probe is not evidence of 30 seconds of outage.
      const confirmed = await isDaemonRunning(this.port).catch(() => false);
      if (
        this.stopped ||
        generation !== this.stabilityGeneration ||
        this.currentReported === expected
      )
        return;
      if (confirmed !== expected) {
        if (this.stabilityTimer) clearTimeout(this.stabilityTimer);
        this.stabilityTimer = null;
        this.pendingState = this.currentReported;
        this.stabilityGeneration++;
        return;
      }
      const prev = this.currentReported;
      this.currentReported = expected;
      logger.info({ from: prev, to: expected }, 'DaemonWatcher: stable state change');
      for (const cb of this.subscribers) {
        try {
          cb(expected);
        } catch (err) {
          logger.warn({ err }, 'DaemonWatcher: subscriber threw');
        }
      }
    } finally {
      if (this.confirmingGeneration === generation) this.confirmingGeneration = null;
    }
  }
}
